// Minimum minor league system: total AND composition, from the "Min Minor League Players" setting (default 20).
// Targets scale with that one number:
//   position players  = ceil(55% of the minimum)  -> 11 of 20
//   pitchers          = the rest                  ->  9 of 20
//   catchers able to catch: 2 (one plus an alternative), starters able to start games: 5, both capped by their group
//   coverage of the 8 field positions, using secondary positions, once the position-player target is at least 8
// Each player counts in ONE group, by his listed position (SP/RP = pitcher), so nobody fills both. There is no
// maximum: a system above the minimum can still be short on composition, and fixing it can take it above.
import type { Player } from "../../../common/types.ts";

export const FIELD_POSITIONS = ["C", "1B", "2B", "3B", "SS", "LF", "CF", "RF"] as const;

const ratings = (p: Player) => p.ratings.at(-1)!;
export const isPitcherGroup = (p: Player) => ["SP", "RP"].includes(ratings(p).pos);

// A position player covers a position when he's nearly as good there as at his best field position
export const coversPosition = (p: Player, pos: string) => {
	if (isPitcherGroup(p)) return false;
	const ovrs = (ratings(p).ovrs ?? {}) as Record<string, number>;
	const best = Math.max(0, ...FIELD_POSITIONS.map((x) => ovrs[x] ?? 0));
	return (ovrs[pos] ?? 0) >= Math.max(35, best - 8);
};
export const canStart = (p: Player) => {
	if (!isPitcherGroup(p)) return false;
	const ovrs = (ratings(p).ovrs ?? {}) as Record<string, number>;
	return ratings(p).pos === "SP" || (ovrs.SP ?? 0) >= Math.max(35, (ovrs.RP ?? 0) - 3);
};

export const minorsTargets = (min: number) => {
	const fielders = Math.ceil(min * 0.55);
	const pitchers = Math.max(0, min - fielders);
	return {
		total: min,
		fielders,
		pitchers,
		catchers: Math.min(2, fielders),
		starters: Math.min(5, pitchers),
		coverPositions: fielders >= FIELD_POSITIONS.length,
	};
};

export const minorsStatus = (players: Player[], min: number) => {
	const t = minorsTargets(min);
	const fielders = players.filter((p) => !isPitcherGroup(p));
	const pitchers = players.filter(isPitcherGroup);
	const catchers = fielders.filter((p) => coversPosition(p, "C")).length;
	const starters = pitchers.filter(canStart).length;
	const uncovered = t.coverPositions ? FIELD_POSITIONS.filter((pos) => !fielders.some((p) => coversPosition(p, pos))) : [];
	return {
		targets: t,
		total: players.length,
		fielders: fielders.length,
		pitchers: pitchers.length,
		catchers,
		starters,
		uncovered: [...uncovered],
		short: {
			total: Math.max(0, t.total - players.length),
			fielders: Math.max(0, t.fielders - fielders.length),
			pitchers: Math.max(0, t.pitchers - pitchers.length),
			catchers: Math.max(0, t.catchers - catchers),
			starters: Math.max(0, t.starters - starters),
			positions: uncovered.length,
		},
	};
};

export type MinorsStatus = ReturnType<typeof minorsStatus>;

export const hasShortage = (s: MinorsStatus) => Object.values(s.short).some((x) => x > 0);

// How much signing p would reduce the shortages (0 = doesn't help, so he isn't signed)
export const shortageReduction = (before: MinorsStatus, after: MinorsStatus) =>
	3 * (before.short.fielders - after.short.fielders) +
	3 * (before.short.pitchers - after.short.pitchers) +
	2 * (before.short.positions - after.short.positions) +
	2 * (before.short.catchers - after.short.catchers) +
	2 * (before.short.starters - after.short.starters) +
	(before.short.total - after.short.total);

// Same number as shortageReduction(status, minorsStatus([...players, p], min)), without re-analyzing the organization:
// adding one player lowers each shortage by at most one (positions: by each uncovered one he covers).
export type CandidateTraits = { pitcher: boolean; covers: Set<string>; canStart: boolean };
export const candidateTraits = (p: Player): CandidateTraits => ({
	pitcher: isPitcherGroup(p),
	covers: new Set(FIELD_POSITIONS.filter((pos) => coversPosition(p, pos))),
	canStart: canStart(p),
});
export const quickReduction = (s: MinorsStatus, c: CandidateTraits) =>
	(c.pitcher ? (s.short.pitchers > 0 ? 3 : 0) : s.short.fielders > 0 ? 3 : 0) +
	2 * s.uncovered.filter((pos) => c.covers.has(pos)).length +
	(s.short.catchers > 0 && c.covers.has("C") ? 2 : 0) +
	(s.short.starters > 0 && c.canStart ? 2 : 0) +
	(s.short.total > 0 ? 1 : 0);
